import Cookies from 'js-cookie'
import mixpanel from 'mixpanel-browser'
import { Currency, User } from '../gql/types'
import { Params } from './params'

const MIXPANEL_KEY = 'fb012a735cb2ac12beb75a5b79d8ef25'

// Returns the registrable apex (e.g. 'useclip.com' for 'app.useclip.com')
// so cookies set here propagate across landing + app subdomains. Falls back
// to the raw hostname for IPs and localhost.
const getCookieDomain = (): string | undefined => {
  if (typeof window === 'undefined') return undefined
  const host = window.location.hostname
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return undefined
  }
  const parts = host.split('.')
  if (parts.length < 2) return undefined
  return `.${parts.slice(-2).join('.')}`
}

// Meta's documented _fbc format: fb.{subdomain_index}.{timestamp_ms}.{fbclid}
// We use subdomain_index=1 (we always set on apex). Reconstructing from a
// stored fbclid lets us recover when the fbq pixel hasn't initialised yet or
// when Safari ITP / blockers wipe Meta's own cookie.
const buildFbcFromFbclid = (fbclid: string): string => {
  return `fb.1.${Date.now()}.${fbclid}`
}

// Always read the latest Meta cookies right before tracking. Falls back to a
// synthetic _fbc built from the stored fbclid if Meta's own cookie is missing.
export const getMetaCookies = (): { fbp?: string; fbc?: string } => {
  if (typeof window === 'undefined') return {}

  const fbp = Cookies.get('_fbp')
  let fbc = Cookies.get('_fbc')

  if (!fbc) {
    const fbclid = localStorage.getItem(Params.fbclid)
    if (fbclid) {
      fbc = buildFbcFromFbclid(fbclid)
      // Persist so the next event reuses the same timestamp — Meta cares about
      // the moment of click, not the moment of read.
      Cookies.set('_fbc', fbc, {
        domain: getCookieDomain(),
        expires: 90,
        sameSite: 'lax',
      })
    }
  }

  return { fbp, fbc }
}

interface CreateTrackPurchaseInput {
  transactionId: string
  currency: Currency
  priceCents: number
  email: string
}

const trackPurchaseInputDefault = {
  transactionId: null,
  currency: null,
  priceCents: null,
  email: null,
}

export const init = () => {
  mixpanel.init(MIXPANEL_KEY, {
    debug: false,
    // Share distinct_id across landing + app subdomains so events from both
    // sides land on the same user. This requires cookie persistence (the
    // default) — localStorage is origin-scoped and would break sharing.
    cross_subdomain_cookie: true,
  })
}

// Called once we know who the user is. Identifies them in Mixpanel so that
// past anonymous events on this device merge into the identified profile,
// and future server-side mirrors (which use user.id as distinct_id) land on
// the same record.
export const identity = (user: User) => {
  if (!user?.id) return
  try {
    mixpanel.identify(String(user.id))
    mixpanel.people.set({
      $email: user.email,
      $first_name: user.firstName,
      $last_name: user.lastName,
      userType: user.type,
    })
  } catch (error) {
    console.log('Error calling mixpanel.identify', error)
  }
}

export const trackEvent = (event: AnalyticsEvent, options: any = {}): void => {
  mixpanel.track(event, options)

  // @ts-ignore
  window.fbq('trackCustom', event)

  // @ts-ignore
  window.gtag('event', event)

  // @ts-ignore - TikTok
  window.ttq.track(event, options)
}

export enum AnalyticsEvent {
  firstPurchase = 'firstPurchase',

  // App
  viewLogin = 'viewLogin',

  // Brands events
  viewBrandsProfile = 'viewBrandsProfile',
  viewBrandsRegister = 'viewBrandsRegister',
  brandSignedUp = 'brandSignedUp',
  startTrial = 'startTrial',
  completeRegistration = 'completeRegistration',
  clickBookOnboardingCall = 'clickBookOnboardingCall',
  clickBookADemoCall = 'clickBookADemoCall',
  brandClickGetInTouch = 'brandClickGetInTouch',
  brandClickBookAnOnboardingCall = 'brandClickBookAnOnboardingCall',
  brandDownloadClip = 'brandDownloadClip',
  viewBrandsTemplates = 'viewBrandsTemplates',

  // Brands onboarding
  viewBrandsOnboardingSetSourceChannel = 'viewBrandsOnboardingSetSourceChannel',
  viewBrandsOnboardingSetWebsite = 'viewBrandsOnboardingSetWebsite',
  viewBrandsOnboardingSetDetails = 'viewBrandsOnboardingSetDetails',
  viewBrandsOnboardingSetBrandIndustry = 'viewBrandsOnboardingSetBrandIndustry',
  viewBrandsOnboardingSetCampaignDetails = 'viewBrandsOnboardingSetCampaignDetails',
  viewBrandsOnboardingSetProductDetails = 'viewBrandsOnboardingSetProductDetails',
  viewBrandsOnboardingSetVideoType = 'viewBrandsOnboardingSetVideoType',
  viewBrandsOnboardingSetModelType = 'viewBrandsOnboardingSetModelType',
  viewBrandsOnboardingSetVoiceType = 'viewBrandsOnboardingSetVoiceType',
  viewBrandsOnboardingSetPlatform = 'viewBrandsOnboardingSetPlatform',
  viewBrandsOnboardingSetVideoDuration = 'viewBrandsOnboardingSetVideoDuration',
  viewBrandsOnboardingSetVideoPlacements = 'viewBrandsOnboardingSetVideoPlacements',
  viewBrandsOnboardingSetRequirements = 'viewBrandsOnboardingSetRequirements',
  viewBrandsOnboardingSetAddons = 'viewBrandsOnboardingSetAddons',
  viewBrandsOnboardingSetHooks = 'viewBrandsOnboardingSetHooks',
  viewBrandsOnboardingSetCreatorDetails = 'viewBrandsOnboardingSetCreatorDetails',
  viewBrandsOnboardingSetOrderSummary = 'viewBrandsOnboardingSetOrderSummary',
  viewBrandsAllReviews = 'viewBrandsAllReviews',

  // Brands collaborations
  viewBrandsCollaborationProfile = 'viewBrandsCollaborationProfile',
  viewBrandsCollaborationReviews = 'viewBrandsCollaborationReviews',
  brandLeftCreatorReview = 'brandLeftCreatorReview',
  brandUpdatedRating = 'brandUpdatedRating',

  // checkout
  initiateCheckout = 'initiateCheckout',
  purchase = 'purchase',

  // Creators events
  viewCreatorsRegister = 'viewCreatorsRegister',
  creatorSignedUp = 'creatorSignedUp',
  creatorAppliedToClip = 'creatorAppliedToClip',
  creatorAppliedToCampaign = 'creatorAppliedToCampaign',
  viewCreatorsOpportunities = 'viewCreatorsOpportunities',
  viewCreatorsInvitations = 'viewCreatorsInvitations',
  creatorWithdrawFromCollaboration = 'creatorWithdrawFromCollaboration',
  creatorAcceptedCampaignInvitation = 'creatorAcceptedCampaignInvitation',
  creatorGetInTouch = 'creatorGetInTouch',

  // Creators onboarding
  viewCreatorsOnboardingSetSourceChannel = 'viewCreatorsOnboardingSetSourceChannel',
  viewCreatorsOnboardingSetProfile = 'viewCreatorsOnboardingSetProfile',
  viewCreatorsOnboardingSetAbout = 'viewCreatorsOnboardingSetAbout',
  viewCreatorsOnboardingSetInterests = 'viewCreatorsOnboardingSetInterests',
  viewCreatorsOnboardingSetIntroVideo = 'viewCreatorsOnboardingSetIntroVideo',
  viewCreatorsOnboardingSetShippingAddress = 'viewCreatorsOnboardingSetShippingAddress',
  viewCreatorsOnboardingSetPaypal = 'viewCreatorsOnboardingSetPaypal',
  viewCreatorsOnboardingSetAgreeTerms = 'viewCreatorsOnboardingSetAgreeTerms',
  viewCreatorsOnboardingSetPortfolio = 'viewCreatorsOnboardingSetPortfolio',
  viewCreatorsOnboardingApplicationCompleted = 'viewCreatorsOnboardingApplicationCompleted',
  viewCreatorsOnboardingApplicationRejected = 'viewCreatorsOnboardingApplicationRejected',

  // Contract signing
  viewContractSigningPage = 'viewContractSigningPage',
  contractSigned = 'contractSigned',

  // Creators collaborations
  viewCreatorsCollaborationView = 'viewCreatorsCollaborationView',
  viewCreatorsCollaborationReviews = 'viewCreatorsCollaborationReviews',
  creatorLeftBrandReview = 'creatorLeftBrandReview',

  // Account Settings
  viewCreatorsProfile = 'viewCreatorsProfile',
  viewCreatorsPersonalInformation = 'viewCreatorsPersonalInformation',
  viewCreatorsEditShippingAddress = 'viewCreatorsEditShippingAddress',
  viewCreatorsEditSocialMedia = 'viewCreatorsEditSocialMedia',
  viewCreatorsEditPassword = 'viewCreatorsEditPassword',
  viewCreatorsEditPaused = 'viewCreatorsEditPaused',

  // Team and Invitations
  teamMemberSignedUp = 'teamMemberSignedUp',
}
/**
 * Tracking complete registration for brands - completed onboarding
 */

interface CompleteRegistrationInput {
  userId?: string | number
  userType?: string
}

export const trackCompleteRegistration = ({
  userId,
  userType,
}: CompleteRegistrationInput = {}) => {
  const eventID = userId ? `CompleteRegistration-${userId}` : undefined

  // @ts-ignore - Facebook
  window.fbq(
    'track',
    'CompleteRegistration',
    userType ? { content_name: userType } : {},
    eventID ? { eventID } : undefined,
  )

  mixpanel.track(
    AnalyticsEvent.brandSignedUp,
    eventID
      ? { $insert_id: `brandSignedUp-${userId}`, userType }
      : undefined,
  )

  // @ts-ignore
  window.gtag('event', AnalyticsEvent.brandSignedUp)

  // @ts-ignore
  window.gtag('event', 'complete_registration')

  // @ts-ignore - LinkedIn
  window.lintrk('track', { conversion_id: 19567330 })

  // @ts-ignore - TikTok
  window.ttq.track(
    'CompleteRegistration',
    userType ? { content_name: userType } : {},
    eventID ? { event_id: eventID } : undefined,
  )
}

/**
 * this is tracking completing the onboarding and getting to checkout page
 */
export const trackAddToCart = () => {
  // @ts-ignore - Facebook
  window.fbq('track', 'AddToCart')

  // @ts-ignore - Google
  window.gtag('event', 'add_to_cart')

  // Mixpanel
  mixpanel.track(AnalyticsEvent.brandSignedUp)

  // @ts-ignore - LinkedIn
  window.lintrk('track', { conversion_id: 19567338 })

  // @ts-ignore - TikTok
  window.ttq.track('AddToCard')
}

interface InitiateCheckoutInput {
  insertId?: string
  value?: number
  currency?: string
  contentIds?: string[]
  numItems?: number
}

export const trackInitiateCheckout = ({
  insertId,
  value,
  currency,
  contentIds,
  numItems,
}: InitiateCheckoutInput = {}) => {
  const fbCustomData: Record<string, any> = {}
  if (value !== undefined && currency) {
    fbCustomData.value = value
    fbCustomData.currency = currency.toLocaleUpperCase()
  }
  if (contentIds?.length) {
    fbCustomData.content_ids = contentIds
    fbCustomData.content_type = 'product'
    fbCustomData.contents = contentIds.map((id) => ({
      id,
      quantity: numItems ?? 1,
    }))
  }
  if (numItems !== undefined) {
    fbCustomData.num_items = numItems
  }

  // @ts-ignore - Facebook
  window.fbq(
    'track',
    'InitiateCheckout',
    fbCustomData,
    insertId ? { eventID: `InitiateCheckout-${insertId}` } : undefined,
  )

  // @ts-ignore - Google
  window.gtag('event', 'begin_checkout', {
    value,
    currency: currency?.toLocaleUpperCase(),
  })

  // Mixpanel — pass $insert_id so retries from Stripe cancel/back dedupe to one event
  mixpanel.track(AnalyticsEvent.initiateCheckout, {
    ...(insertId ? { $insert_id: `initiateCheckout-${insertId}` } : {}),
    value,
    currency: currency?.toLocaleUpperCase(),
    numItems,
  })

  // @ts-ignore - LinkedIn
  window.lintrk('track', { conversion_id: 19567346 })

  // @ts-ignore - TikTok
  window.ttq.track(
    'InitiateCheckout',
    {
      ...fbCustomData,
      contents: contentIds?.map((id) => ({
        content_id: id,
        content_type: 'product',
      })),
    },
    insertId ? { event_id: `InitiateCheckout-${insertId}` } : undefined,
  )
}

export const trackPurchase = (
  input: CreateTrackPurchaseInput = trackPurchaseInputDefault,
) => {
  const price = input.priceCents / 100
  const currency = input.currency.toLocaleUpperCase()
  const contentId = `order-${input.transactionId}`

  // @ts-ignore Facebook
  window.fbq(
    'track',
    'Purchase',
    {
      value: price,
      currency,
      content_ids: [contentId],
      content_type: 'product',
      contents: [{ id: contentId, quantity: 1, item_price: price }],
      num_items: 1,
      order_id: input.transactionId,
    },
    { eventID: `Purchase-${input.transactionId}` },
  )

  // Mixpanel — $insert_id dedupes if both the client and the server-side
  // mirror fire (or the React effect double-runs).
  mixpanel.track(AnalyticsEvent.purchase, {
    $insert_id: `purchase-${input.transactionId}`,
    transactionId: input.transactionId,
    value: input.priceCents / 100,
    currency: input.currency.toLocaleUpperCase(),
  })

  // @ts-ignore
  window.gtag('event', 'purchase', {
    transaction_id: input.transactionId, // A unique transaction ID for this purchase.
    affiliation: 'Clip App', // The store or affiliation from which this transaction occurred (if applicable).
    value: input.priceCents / 100, // The total value of the transaction.
    currency: input.currency.toLocaleUpperCase(), // The currency of the transaction.
    tax: 0.0, // The total tax associated with the transaction.
    shipping: 0.0, // The shipping cost associated with the transaction.
  })

  // @ts-ignore LinkedIn
  window.lintrk('track', {
    conversion_id: 19567354,
    transaction_id: input.transactionId, // A unique transaction ID for this purchase.
    value: input.priceCents / 100, // The total value of the transaction.
    currency: input.currency.toLocaleUpperCase(), // The currency of the transaction.
  })

  // @ts-ignore - TikTok
  window.ttq.track(
    'CompletePayment',
    {
      contents: [
        {
          content_id: input.transactionId,
          content_type: 'product',
          content_name: 'purchase',
        },
      ],
      value: input.priceCents / 100,
      currency: input.currency.toLocaleUpperCase(),
    },
    {
      event_id: `purchase-${input.transactionId}`,
    },
  )

  if (input.email) {
    // @ts-ignore
    window.rewardful('convert', { email: input.email })
  }
}

export const trackFirstPurchase = (
  input: CreateTrackPurchaseInput = trackPurchaseInputDefault,
) => {
  const price = input.priceCents / 100
  const currency = input.currency.toLocaleUpperCase()
  const contentId = `order-${input.transactionId}`

  // @ts-ignore Facebook
  window.fbq(
    'track',
    AnalyticsEvent.firstPurchase,
    {
      value: price,
      currency,
      content_ids: [contentId],
      content_type: 'product',
      contents: [{ id: contentId, quantity: 1, item_price: price }],
      num_items: 1,
      order_id: input.transactionId,
    },
    { eventID: `firstPurchase-${input.transactionId}` },
  )

  // Mixpanel — $insert_id dedupes if both the client and the server-side
  // mirror fire (or the React effect double-runs).
  mixpanel.track(AnalyticsEvent.firstPurchase, {
    $insert_id: `firstPurchase-${input.transactionId}`,
    transactionId: input.transactionId,
    value: input.priceCents / 100,
    currency: input.currency.toLocaleUpperCase(),
  })

  // @ts-ignore
  window.gtag('event', AnalyticsEvent.firstPurchase)

  // @ts-ignore LinkedIn
  window.lintrk('track', {
    conversion_id: 19567362,
    transaction_id: input.transactionId, // A unique transaction ID for this purchase.
    value: input.priceCents / 100, // The total value of the transaction.
    currency: input.currency.toLocaleUpperCase(), // The currency of the transaction.
  })

  // @ts-ignore - TikTok
  window.ttq.track(
    'firstPurchase',
    {
      contents: [
        {
          content_id: input.transactionId,
          content_type: 'product',
          content_name: 'purchase',
        },
      ],
      value: input.priceCents / 100,
      currency: input.currency.toLocaleUpperCase(),
    },
    {
      event_id: `firstPurchase-${input.transactionId}`,
    },
  )
}
